import { useState } from "react";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import { OrgInviteRecord, OrgRecord } from "@/shared/domain/orgs";

export const useSubmitVerificationAndSignIn = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const { submitVerificationCode, signIn } = useAuth();
  const { users: userService } = useDomainServices();

  const handleVerificationCodeSubmission = async (
    emailAddress: string,
    code: string,
  ): Promise<string | null> => {
    setLoading(true);
    setErrMsg(null);

    const submitErr = await submitVerificationCode(emailAddress, code);
    if (submitErr) {
      setErrMsg(submitErr);
      setLoading(false);
    }
    return submitErr;
  };

  const handleSignIn = async (
    emailAddress: string,
    password: string,
  ): Promise<string | null> => {
    const signInErr = await signIn(emailAddress, password);
    if (signInErr) {
      setErrMsg(signInErr);
      setLoading(false);
    }
    return signInErr;
  };

  const fetchOrganizationsAndInvites = async (): Promise<{
    organizations: OrgRecord[];
    invites: OrgInviteRecord[];
    errMsg: string | null;
  }> => {
    try {
      const [orgs, invites] = await Promise.all([
        userService.getOrgsForCurrentUser(),
        userService.getInvitesForCurrentUser(),
      ]);
      setLoading(false);
      return { organizations: orgs, invites: invites, errMsg: null };
    } catch (e: unknown) {
      if (e instanceof Error) {
        setErrMsg(e.message);
        setLoading(false);
        return { organizations: [], invites: [], errMsg: e.message };
      }
      return {
        organizations: [],
        invites: [],
        errMsg: "Something went wrong. Please try again.",
      };
    }
  };

  const submitVerificationAndSignIn = async (
    emailAddress: string,
    code: string,
    password: string,
  ): Promise<{
    organizations: OrgRecord[];
    invites: OrgInviteRecord[];
    errMsg: string | null;
  }> => {
    const submitErr = await handleVerificationCodeSubmission(
      emailAddress,
      code,
    );

    // We're doing string parsing here because Amplify doesn't have a specific enough error type for this case
    // If we upgrade Amplify we should verify this still works
    if (submitErr && !submitErr.includes("Current status is CONFIRMED")) {
      return { organizations: [], invites: [], errMsg: submitErr };
    }

    const signInErr = await handleSignIn(emailAddress, password);
    if (signInErr) {
      return { organizations: [], invites: [], errMsg: signInErr };
    }

    return await fetchOrganizationsAndInvites();
  };

  const clearErrMsg = () => {
    setErrMsg(null);
  };

  return { loading, errMsg, submitVerificationAndSignIn, clearErrMsg };
};
